import {Injectable, Injector} from '@angular/core';
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
} from '@angular/common/http';
import {Observable} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {ToastrService} from 'ngx-toastr';
import {z} from 'zod';
import {TranslateService} from '@brunata-angular/util';
import {TokenRequestError} from '@brunata-angular/auth';

export const interceptorSkipHeader = 'X-Skip-Interceptor';

const webserviceErrorSchema = z.object({errorMessage: z.string().trim().min(1)});

export function errorBody(error: unknown): unknown {
    if (typeof error !== 'string') {
        return error;
    }
    try {
        return JSON.parse(error);
    } catch {
        return null;
    }
}

/**
 * Intercepts the HTTP responses, and in case that an error/exception is thrown, handles it
 * and extract the relevant information of it.
 */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
    constructor(
        private toastr: ToastrService,
        private injector: Injector,
    ) {}

    /**
     * Intercepts an outgoing HTTP request, executes it and handles any error that could be triggered in execution.
     * @see HttpInterceptor
     * @param req the outgoing HTTP request
     * @param next a HTTP request handler
     */
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Skip requests with the 'skip header'
        if (req.headers.has(interceptorSkipHeader)) {
            const headers = req.headers.delete(interceptorSkipHeader);
            return next.handle(req.clone({headers}));
        }

        return next.handle(req).pipe(
            catchError(errorResponse => {
                if (errorResponse instanceof HttpErrorResponse) {
                    // We have to inject TranslateService this way to avoid cyclic dependency
                    const translateService = this.injector.get(TranslateService);
                    const genericMessage = translateService.translate(
                        'ERROR_OCCURRED',
                        'online',
                        'common',
                    );
                    const webserviceError = webserviceErrorSchema.safeParse(
                        errorBody(errorResponse.error),
                    );
                    if (webserviceError.success) {
                        this.toastr.error(webserviceError.data.errorMessage, genericMessage);
                    } else {
                        this.toastr.error(genericMessage);
                    }
                } else if (errorResponse instanceof TokenRequestError) {
                    const translateService = this.injector.get(TranslateService);
                    this.toastr.error(
                        translateService.translate('ERROR_OCCURRED', 'online', 'common'),
                    );
                } else {
                    // todo: add more later on.
                }
                // pass on the errorResponse
                throw errorResponse;
            }),
        );
    }
}
